// ---------------------------------------------------------------------------
// ChannelCardFooter — 3-state status badge + Created date + last-analysed.
//
// Status priority (polish batch 1):
//   1. scrapingEnabled === false                           → PAUSED       (grey)
//   2. lastAnalysedAt === null OR daysSince(...) > 7       → ACTIVE—STALE (amber)
//   3. otherwise                                           → ACTIVE       (green)
//
// The old "NOT YET PROCESSED" (red) state was merged into ACTIVE — STALE:
// operator-level, "never analysed" and "last analysis >7d" communicate the
// same thing to the user (the numbers on the card may be out of date), and
// in local dev the red state fired on every channel making it useless.
// ---------------------------------------------------------------------------

import React, { useMemo } from 'react';
import type { ChannelCardFooterProps } from './types';

type Status = 'paused' | 'active-stale' | 'active';

const STATUS_CONFIG: Readonly<
  Record<
    Status,
    {
      readonly label: string;
      readonly dot: string;
      readonly text: string;
      readonly tooltip: string;
    }
  >
> = {
  paused: {
    label: 'PAUSED',
    // Muted state — use the v11-text-muted token so both themes render the
    // same semantic "subdued" grey. Raw `gray-400` in light mode washed out
    // against the light card surface.
    dot: 'bg-v11-text-muted',
    text: 'text-v11-text-muted',
    tooltip: 'Scraping is paused for this channel.',
  },
  'active-stale': {
    label: 'ACTIVE — STALE',
    dot: 'bg-amber-400',
    text: 'text-amber-400',
    tooltip:
      'Scraping is on but the last analysis is more than 7 days old (or has never completed yet).',
  },
  active: {
    label: 'ACTIVE',
    dot: 'bg-green-400',
    text: 'text-green-400',
    tooltip: 'Scraping is running and analysis is recent.',
  },
};

export function ChannelCardFooter({ channel }: ChannelCardFooterProps) {
  const status = useMemo<Status>(() => {
    if (channel.scrapingEnabled === false) return 'paused';
    if (channel.lastAnalysedAt === null) return 'active-stale';
    return daysSince(channel.lastAnalysedAt) > 7 ? 'active-stale' : 'active';
  }, [channel.lastAnalysedAt, channel.scrapingEnabled]);

  const cfg = STATUS_CONFIG[status];
  const createdLabel = useMemo(() => formatCreated(channel.createdAt), [channel.createdAt]);
  const analysedLabel = useMemo(
    () => formatRelative(channel.lastAnalysedAt),
    [channel.lastAnalysedAt]
  );

  return (
    <div className="px-4 py-2.5 border-t border-v11-border flex items-center justify-between gap-2">
      <span
        data-status={status}
        title={cfg.tooltip}
        className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider cursor-help"
      >
        <span className={`h-2 w-2 rounded-full ${cfg.dot}`} />
        <span className={cfg.text}>{cfg.label}</span>
      </span>

      <div className="flex items-center gap-3 text-[10px] text-v11-text-muted">
        <span title="Channel created on">Created {createdLabel}</span>
        <span aria-label="Last analysed" title="Last analysed">
          {analysedLabel}
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

/**
 * Backend timestamp strings come in two shapes:
 *   - `YYYY-MM-DD`                  (createdAt, date-only)
 *   - `YYYY-MM-DD HH:MM:SS`          (lastAnalysedAt, UTC, no tz suffix)
 * Both must be treated as UTC. `new Date(spaceSeparated)` parses as LOCAL
 * time in some engines, so we convert to ISO-with-Z first.
 */
function parseUtc(dateStr: string): number {
  if (!dateStr) return NaN;
  if (dateStr.includes('T')) return new Date(dateStr).getTime();
  const hasTime = dateStr.includes(' ');
  const iso = hasTime ? `${dateStr.replace(' ', 'T')}Z` : `${dateStr}T00:00:00Z`;
  return new Date(iso).getTime();
}

function daysSince(dateStr: string): number {
  const t = parseUtc(dateStr);
  if (Number.isNaN(t)) return Infinity;
  return Math.floor((Date.now() - t) / 86_400_000);
}

function formatCreated(dateStr: string): string {
  const t = parseUtc(dateStr);
  if (Number.isNaN(t)) return '—';
  const d = new Date(t);
  const day = String(d.getUTCDate()).padStart(2, '0');
  const month = d.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
  const year = d.getUTCFullYear();
  return `${day} ${month} ${year}`;
}

function formatRelative(dateStr: string | null): string {
  if (dateStr === null) return '—';
  const t = parseUtc(dateStr);
  if (Number.isNaN(t)) return '—';
  const diffMs = Date.now() - t;
  const diffHours = Math.floor(diffMs / 3_600_000);
  if (diffHours < 1) return 'now';
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffMs / 86_400_000);
  if (diffDays < 30) return `${diffDays}d ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
  return `${Math.floor(diffDays / 365)}y ago`;
}
